// Ispis povijesne kartice osnovnog sredstva u pdf.
//
// Jedan red po godini za jedno sredstvo (llaID). Bez pozadinskih boja - tekuca godina se razlikuje
// debljinom slova, upozorenja crvenim slovima i znakom "!".

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import dayjs from 'dayjs'
import {podaciSredstvaTekst} from './llaKarticaPodaci'
import {
  novac, rasporedi, zaglavlje, podnozje, layoutTablice, redNaslova
} from './mainLedgerPdf'

const CRVENA = '#8c2f1e'
const SIVA = '#555555'

// Nula se pise kao 0,00 - u kartici je i nula podatak (potpuno amortizirano sredstvo).
const iznos = (v) => novac(v)
const stopaTekst = (g) => {
  if (g.nemaZapisa) { return '' }
  if (g.stopa !== null && g.stopa !== undefined) { return novac(g.stopa) }
  return novac(g.stopaMin) + '–' + novac(g.stopaMax)
}

export const koloneKartice = () => rasporedi([
  {key: 'godina', head: 'Godina', w: 150, align: 'left'},
  {key: 'nabavna', head: 'Nabavna vrijednost', w: 80, align: 'right'},
  {key: 'ispPoc', head: 'Ispravak početak', w: 80, align: 'right'},
  {key: 'amort', head: 'Amortizacija godine', w: 80, align: 'right'},
  {key: 'ispKraj', head: 'Ispravak kraj', w: 80, align: 'right'},
  {key: 'neto', head: 'Neto kraj godine', w: 80, align: 'right'},
  {key: 'stopa', head: 'Stopa %', w: 58, align: 'right'},
  {key: 'napomena', head: 'Napomena', w: 176, align: 'left', flex: true}
])

const celija = (text, c, opc) => Object.assign({
  text: text,
  alignment: c.align,
  fontSize: 7.5,
  margin: [c.align === 'left' ? 3 : 2, 2.5, c.align === 'right' ? 4 : 2, 2.5]
}, opc || {})

const napomenaTekst = (napomene, upozorenja) => {
  const dijelovi = []
  napomene.forEach(n => dijelovi.push({text: n + '\n', color: SIVA}))
  upozorenja.forEach(u => dijelovi.push({text: '! ' + u + '\n', color: CRVENA}))
  if (dijelovi.length) {
    const zadnji = dijelovi[dijelovi.length - 1]
    zadnji.text = zadnji.text.replace(/\n$/, '')
  }
  return dijelovi.length ? dijelovi : ''
}

const redGodine = (cols, g) => {
  const b = !!g.tekuca
  const opc = {bold: b, fontSize: b ? 8 : 7.5}
  const siv = g.nemaZapisa ? {color: SIVA} : {}
  const o = Object.assign({}, opc, siv)
  const prazno = g.nemaZapisa
  return [
    celija(g.godina + (g.tekuca ? ' (tekuća)' : ''), cols[0], Object.assign({}, o, {bold: true})),
    celija(prazno ? '' : iznos(g.nabavna), cols[1], o),
    celija(prazno ? '' : iznos(g.ispravakPocetak), cols[2], o),
    celija(prazno ? '' : iznos(g.amortizacija), cols[3], o),
    celija(prazno ? '' : iznos(g.ispravakKraj), cols[4], o),
    celija(prazno ? '' : iznos(g.netoKraj), cols[5], o),
    celija(stopaTekst(g), cols[6], o),
    celija(napomenaTekst(g.napomene, g.upozorenja), cols[7], {fontSize: 7})
  ]
}

// ---- detalj knjizenja u godini (redovi unutar iste tablice, spajanjem kolona) ----
// Raspored: [0] stavka/temeljnica | [1] konto | [2..3] naziv konta | [4] duguje | [5] potrazuje | [6..7] opis/napomena
const mala = (text, c, opc) => Object.assign({text: text, fontSize: 6.5, alignment: c.align, margin: [c.align === 'left' ? 3 : 2, 1.5, c.align === 'right' ? 4 : 2, 1.5]}, opc || {})
const sp = () => ({text: ''})
const red8 = (cols, a, b, c, d, e, f, opc) => [
  mala(a, cols[0], opc),
  mala(b, cols[1], Object.assign({alignment: 'left'}, opc || {})),
  mala(c, cols[2], Object.assign({colSpan: 2, alignment: 'left'}, opc || {})), sp(),
  mala(d, cols[4], opc),
  mala(e, cols[5], opc),
  mala(f, cols[6], Object.assign({colSpan: 2, alignment: 'left'}, opc || {})), sp()
]
const iznosIliPrazno = (v) => (v === null || v === undefined ? '' : novac(v))

const infoRed = (cols, tekst) => [
  mala(tekst, cols[0], Object.assign({colSpan: 8, alignment: 'left', color: SIVA}, {})), sp(), sp(), sp(), sp(), sp(), sp(), sp()
]

export const detaljGodine = (cols, g) => {
  const z = g.knjizenje
  if (!z) { return [] }
  const redovi = []
  redovi.push(red8(cols, 'Knjiženje u godini ' + g.godina + ' – temeljnice', 'Konto', 'Naziv konta', 'Duguje', 'Potražuje', 'Opis', {bold: true}))
  const tem = z.temeljnice || []
  if (!tem.length) {
    redovi.push(infoRed(cols, 'nema temeljnice u ovoj godini'))
  }
  tem.forEach(t => {
    t.stavke.forEach((x, i) => {
      redovi.push(red8(cols, i === 0 ? 'OS ' + t.bookID + ' · ' + t.datum + ' (id ' + t.id + ')' : '', x.konto, x.nazivKonta,
        iznos(x.duguje), iznos(x.potrazuje), x.opis))
    })
  })
  if (tem.length) {
    const zb = z.temeljniceZbroj
    redovi.push(red8(cols, 'Zbroj temeljnica', '', 'amortizacija godine ' + novac(g.amortizacija), novac(zb.duguje), novac(zb.potrazuje), '', {bold: true}))
  }
  if (z.nesigurnaVeza) {
    redovi.push(infoRed(cols, '! veza stavki sa sredstvom je po nazivu, a naziv dijeli više sredstava u ovoj godini'))
  }
  if ((z.stavke || []).length) {
    redovi.push(red8(cols, 'Zapis sredstva u godini', 'Konto', 'Naziv konta', 'Duguje', 'Potražuje', 'Napomena', {bold: true}))
    z.stavke.forEach(x => {
      redovi.push(red8(cols, x.stavka, x.konto, x.nazivKonta, iznosIliPrazno(x.duguje), iznosIliPrazno(x.potrazuje), x.napomena))
    })
  }
  return redovi
}

const redUkupno = (cols, k) => [
  celija('Zbroj amortizacije kroz sve godine', cols[0], {bold: true, colSpan: 3}),
  {text: ''},
  {text: ''},
  celija(iznos(k.ukupnoAmortizacija), cols[3], {bold: true}),
  celija('', cols[4]),
  celija('', cols[5]),
  celija('', cols[6]),
  celija('', cols[7])
]

export const buildLlaKarticaPdf = (ctx) => {
  const k = ctx.kartica || {godine: []}
  const company = ctx.company || {}
  const cols = koloneKartice()
  const trenutak = dayjs().format('DD.MM.YYYY HH:mm')

  const body = [redNaslova(cols, 7)]
  const jake = [1]
  const otvorene = ctx.otvorene || []
  k.godine.forEach(g => {
    body.push(redGodine(cols, g))
    if (otvorene.indexOf(g.godina) !== -1) {
      detaljGodine(cols, g).forEach(r => body.push(r))
      jake.push(body.length)
    }
  })
  jake.push(body.length)
  body.push(redUkupno(cols, k))

  const z = k.sazetak
  const sazetakTekst = z
    ? 'Stanje na kraju ' + z.godina + (z.uEvidencijiTekuce ? '' : ' (zadnja godina sa zapisom)') + ': nabavna vrijednost ' + novac(z.nabavna) +
      ', ukupni ispravak vrijednosti ' + novac(z.ispravakKraj) + ', neto vrijednost ' + novac(z.netoKraj) + '.'
    : ''
  const upozorenja = k.upozorenja || []

  const sadrzaj = [
    zaglavlje(
      company,
      'Povijesna kartica osnovnog sredstva',
      (k.naziv || '') + ' · Šifra int. ' + (k.llaID || '') + ' · AOP oznaka ' + (k.llaCode || ''),
      'EUR · godine ' + k.prvaGodina + ' – ' + k.tekucaGodina + (k.stopaTekuca !== null && k.stopaTekuca !== undefined ? ' · stopa ' + novac(k.stopaTekuca) + ' %' : ''),
      ['Datum ispisa: ' + trenutak].concat(podaciSredstvaTekst(k) ? [podaciSredstvaTekst(k)] : []).concat(k.napomene || [])
    ),
    {
      table: {headerRows: 1, widths: cols.map(c => c.w), body: body},
      layout: layoutTablice(jake)
    },
    {text: sazetakTekst, fontSize: 8, bold: true, margin: [0, 6, 0, 0]}
  ]
  if (upozorenja.length) {
    sadrzaj.push({
      text: ['Upozorenja o odstupanjima (podaci nisu ispravljani):\n'].concat(upozorenja.map(u => ({text: u + '\n', bold: false}))),
      fontSize: 7,
      color: CRVENA,
      margin: [0, 6, 0, 0]
    })
  }
  sadrzaj.push({
    text: 'Ispravak na početku je ukupni ispravak vrijednosti na početku godine, na kraju je početni uvećan za amortizaciju godine. ' +
      'Kad se nabavna vrijednost smanji (rashod/prodaja), ispravak se razmjerno preračuna pa ne slijedi iz kraja prethodne godine. ' +
      'Zbroj amortizacije obuhvaća sve prikazane godine.',
    fontSize: 6,
    color: SIVA,
    margin: [0, 6, 0, 0]
  })

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: sadrzaj,
    footer: podnozje([
      company.CompanyNameShort || company.CompanyName || '',
      'Kartica sredstva ' + (k.llaID || '') + ' (AOP ' + (k.llaCode || '') + ')',
      k.naziv || '',
      'ispis ' + trenutak
    ].filter(x => x).join(' · '))
  }
}

export const printLlaKarticaPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildLlaKarticaPdf(ctx)).open()
}

export default {buildLlaKarticaPdf, printLlaKarticaPdf}
